import { useCallback, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { toast } from 'sonner';
import { isBadRequestError } from '@/core/api/badRequestError';
import { assignExecutorService } from '@/entities/Order/api/assignExecutorService';
import { assignExecutorFormToRequest } from '@/entities/Order/assignExecutorMappers';
import type { AssignExecutorFormValues } from '@/entities/Order/model/assignExecutorSchema';
import { getErrorMessage } from '@/shared/lib/errors';

/** Выполняет назначение исполнителя и показывает результат операции. */
export const useAssignExecutorSubmit = (
    orderId: string,
    form: UseFormReturn<AssignExecutorFormValues>,
) => {
    const [completed, setCompleted] = useState(false);
    const [agreementSigningUri, setAgreementSigningUri] = useState<string | null>(null);

    const onSubmit = useCallback(async (values: AssignExecutorFormValues) => {
        form.clearErrors('root.server');

        try {
            const uri = await assignExecutorService.assign(assignExecutorFormToRequest(values, orderId));
            setAgreementSigningUri(uri);
            form.reset(values);
            setCompleted(true);
            toast.success('Исполнитель назначен на заказ');
        } catch (error: unknown) {
            if (isBadRequestError(error)) return;

            form.setError('root.server', {
                type: 'server',
                message: getErrorMessage(error, 'Не удалось назначить исполнителя'),
            });
        }
    }, [form, orderId]);

    /** Очищает результат предыдущего назначения. */
    const resetCompleted = useCallback(() => {
        setCompleted(false);
        setAgreementSigningUri(null);
    }, []);

    return {
        onSubmit,
        completed,
        agreementSigningUri,
        resetCompleted
    };
};
